// What the map draws, as plain data: which layer a deal belongs to, what its
// money is in the map's one currency, how big its bubble is, and the headline
// totals. No MapLibre here, so every rule the picture depends on is tested
// without a WebGL context.
//
// TWO LAYERS. Revenue is a contracted deal (won, implementation, live), drawn
// as a bubble sized by what it has billed. Pipeline is a deal still being sold,
// drawn as a fixed-size dot coloured by stage: most pre-sale deals have no
// contracted value yet (no term lines, or a metered clause), so a size would
// mostly be a guess. Lost deals are not drawn.

import { dealHasCoordinates, type DealListItem, type DealState } from "@crm/shared";
import type { Feature, FeatureCollection, Point } from "geojson";
import { assertNever } from "../../lib/assertNever.js";
import { countryName } from "../../format/countries.js";
import { dealStateLabel } from "../../format/labels.js";
import type { CurrencyView, DisplayAmount } from "../../format/displayCurrency.js";

export const MAP_LAYERS = ["revenue", "pipeline"] as const;
export type MapLayer = (typeof MAP_LAYERS)[number];

/** Which layers the reader asked to see. */
export const MAP_VIEWS = ["both", "revenue", "pipeline"] as const;
export type MapView = (typeof MAP_VIEWS)[number];

/** The pre-sale stages in pipeline order. The index is the colour step. */
export const PIPELINE_STAGES = [
  "lead",
  "qualified",
  "demoed",
  "proposal",
  "decision_maker_bought_in",
] as const satisfies readonly DealState[];

export const BUBBLE_MIN_RADIUS = 6;
export const BUBBLE_MAX_RADIUS = 34;
export const PIPELINE_DOT_RADIUS = 7;

export function mapLayerOf(state: DealState): MapLayer | null {
  switch (state) {
    case "won":
    case "implementation":
    case "live":
    case "ended":
      return "revenue";
    case "lead":
    case "qualified":
    case "demoed":
    case "proposal":
    case "decision_maker_bought_in":
      return "pipeline";
    case "lost":
      return null;
    default:
      return assertNever(state, "deal state");
  }
}

export function showsLayer(view: MapView, layer: MapLayer): boolean {
  return view === "both" || view === layer;
}

/** One deal as the map sees it. */
export interface MapDeal {
  id: string;
  customerName: string;
  partnerName: string;
  state: DealState;
  stateLabel: string;
  layer: MapLayer;
  lng: number;
  lat: number;
  /** "Toronto, Ontario, Canada" — whatever parts were recorded. */
  where: string;
  billed: DisplayAmount;
  /** Null when the deal's whole value is not knowable yet — never a zero. */
  contracted: DisplayAmount | null;
  winProb: number;
}

export function toMapDeals(
  deals: readonly DealListItem[],
  view: CurrencyView,
  today: string,
): MapDeal[] {
  const out: MapDeal[] = [];
  for (const deal of deals) {
    if (!dealHasCoordinates(deal)) continue;
    const layer = mapLayerOf(deal.state);
    if (layer === null) continue;
    out.push({
      id: deal.id,
      customerName: deal.customer_name,
      partnerName: deal.partner_name,
      state: deal.state,
      stateLabel: dealStateLabel(deal.state),
      layer,
      lng: deal.customer_lng,
      lat: deal.customer_lat,
      where: [
        deal.customer_city,
        deal.customer_region,
        deal.customer_country === null ? null : countryName(deal.customer_country),
      ]
        .filter((part): part is string => part !== null && part !== "")
        .join(", "),
      billed: view.amount(deal.billed_to_date_gross_minor, deal.currency, today),
      contracted:
        deal.contracted_gross_minor === null
          ? null
          : view.amount(deal.contracted_gross_minor, deal.currency, today),
      winProb: deal.win_prob,
    });
  }
  return out;
}

export interface MapSummary {
  revenue: {
    billedMinor: number;
    dealCount: number;
    customerCount: number;
    /** Deals left out of `billedMinor` because no FX rate could convert them. */
    unconvertible: number;
  };
  pipeline: {
    dealCount: number;
    /** The sum of the contracted values that are known. */
    knownValueMinor: number;
    /** How many pipeline deals `knownValueMinor` covers. */
    pricedCount: number;
    unconvertible: number;
  };
}

function isInTarget(amount: DisplayAmount): boolean {
  return amount.kind !== "unconvertible";
}

export function summariseMap(deals: readonly MapDeal[]): MapSummary {
  const summary: MapSummary = {
    revenue: { billedMinor: 0, dealCount: 0, customerCount: 0, unconvertible: 0 },
    pipeline: { dealCount: 0, knownValueMinor: 0, pricedCount: 0, unconvertible: 0 },
  };
  const customers = new Set<string>();

  for (const deal of deals) {
    switch (deal.layer) {
      case "revenue":
        summary.revenue.dealCount += 1;
        customers.add(deal.customerName);
        if (isInTarget(deal.billed)) summary.revenue.billedMinor += deal.billed.minor;
        else summary.revenue.unconvertible += 1;
        break;
      case "pipeline":
        summary.pipeline.dealCount += 1;
        if (deal.contracted === null) break;
        if (isInTarget(deal.contracted)) {
          summary.pipeline.knownValueMinor += deal.contracted.minor;
          summary.pipeline.pricedCount += 1;
        } else {
          summary.pipeline.unconvertible += 1;
        }
        break;
      default:
        assertNever(deal.layer, "map layer");
    }
  }

  summary.revenue.customerCount = customers.size;
  return summary;
}

/**
 * Radius from an amount, by AREA: a bubble's size is read as its area, so the
 * square root keeps a deal worth four times as much from looking sixteen times
 * as big. Nothing billed still draws at the minimum, so a newly won deal is on
 * the map.
 */
export function bubbleRadius(minor: number, maxMinor: number): number {
  if (maxMinor <= 0 || minor <= 0) return BUBBLE_MIN_RADIUS;
  const share = Math.sqrt(Math.min(minor, maxMinor) / maxMinor);
  return BUBBLE_MIN_RADIUS + share * (BUBBLE_MAX_RADIUS - BUBBLE_MIN_RADIUS);
}

export type LngLatBounds = [[number, number], [number, number]];

/** `[[west, south], [east, north]]` around every deal, or null with none. */
export function dealBounds(deals: readonly MapDeal[]): LngLatBounds | null {
  if (deals.length === 0) return null;
  let west = Infinity;
  let south = Infinity;
  let east = -Infinity;
  let north = -Infinity;
  for (const deal of deals) {
    west = Math.min(west, deal.lng);
    east = Math.max(east, deal.lng);
    south = Math.min(south, deal.lat);
    north = Math.max(north, deal.lat);
  }
  return [
    [west, south],
    [east, north],
  ];
}

export interface MapFeatureProperties {
  id: string;
  radius: number;
  /** Index into PIPELINE_STAGES; -1 on the revenue layer. */
  stage: number;
}

export function toFeatureCollection(
  deals: readonly MapDeal[],
  layer: MapLayer,
): FeatureCollection<Point, MapFeatureProperties> {
  const onLayer = deals.filter((deal) => deal.layer === layer);
  const sized = (deal: MapDeal): number => (isInTarget(deal.billed) ? deal.billed.minor : 0);
  const maxMinor = Math.max(0, ...onLayer.map(sized));

  return {
    type: "FeatureCollection",
    features: onLayer.map((deal): Feature<Point, MapFeatureProperties> => ({
      type: "Feature",
      geometry: { type: "Point", coordinates: [deal.lng, deal.lat] },
      properties: {
        id: deal.id,
        radius: layer === "revenue" ? bubbleRadius(sized(deal), maxMinor) : PIPELINE_DOT_RADIUS,
        stage: (PIPELINE_STAGES as readonly DealState[]).indexOf(deal.state),
      },
    })),
  };
}
